import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Autocomplete,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Widget,
  ControlWidgetConfiguration,
  ControlFilter,
} from '../../../types/canvas.types';
import { useDashboardFilters } from '../../../contexts/DashboardFilterContext';

interface ControlWidgetProps {
  widget: Widget;
  onEdit?: (widgetId: string) => void;
  onDelete?: (widgetId: string) => void;
  onDuplicate?: (widgetId: string) => void;
}

export const ControlWidget: React.FC<ControlWidgetProps> = ({ widget }) => {
  const config = widget.configuration as ControlWidgetConfiguration;
  const {
    updateFilter,
    clearFilters,
    filters: appliedFilters,
  } = useDashboardFilters();

  const [draftFilterValues, setDraftFilterValues] = useState<
    Record<string, any>
  >({});

  const hasControls =
    (config?.filters && config.filters.length > 0) ||
    (config?.actions && config.actions.length > 0);

  const hasPendingChanges = React.useMemo(() => {
    if (!config?.filters) return false;
    return config.filters.some((filter) => {
      const draftValue = draftFilterValues[filter.field];
      const appliedValue = appliedFilters[filter.field];
      if (Array.isArray(draftValue) && Array.isArray(appliedValue)) {
        return (
          JSON.stringify([...draftValue].sort()) !==
          JSON.stringify([...appliedValue].sort())
        );
      }
      return draftValue !== appliedValue;
    });
  }, [draftFilterValues, appliedFilters, config?.filters]);

  const handleFilterChange = (field: string, value: any) => {
    setDraftFilterValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    Object.entries(draftFilterValues).forEach(([field, value]) => {
      updateFilter(field, value);
    });
  };

  const handleClearFilters = () => {
    setDraftFilterValues({});
    clearFilters();
  };

  if (!hasControls) {
    return (
      <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
        <Typography variant="h6">No controls configured</Typography>
      </Paper>
    );
  }

  const renderFilter = (filter: ControlFilter) => {
    switch (filter.type) {
      case 'multi-select':
        return (
          <Autocomplete
            multiple
            options={filter.options || []}
            getOptionLabel={(option) => option.label}
            value={
              filter.options?.filter((opt) =>
                (draftFilterValues[filter.field] || []).includes(opt.value),
              ) || []
            }
            onChange={(_, newValue) => {
              handleFilterChange(
                filter.field,
                newValue.map((v) => v.value),
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={filter.label}
                variant="outlined"
                size="small"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.value}
                  label={option.label}
                  size="small"
                />
              ))
            }
          />
        );

      case 'select':
        return (
          <FormControl fullWidth size="small">
            <InputLabel>{filter.label}</InputLabel>
            <Select
              value={draftFilterValues[filter.field] || ''}
              label={filter.label}
              onChange={(e) => handleFilterChange(filter.field, e.target.value)}
            >
              {filter.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'date':
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={filter.label}
              value={draftFilterValues[filter.field] || null}
              onChange={(newValue: Date | null) =>
                handleFilterChange(filter.field, newValue)
              }
              slotProps={{
                textField: { fullWidth: true, size: 'small' },
              }}
            />
          </LocalizationProvider>
        );

      case 'date-range':
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <DatePicker
                  label={`${filter.label} (From)`}
                  value={draftFilterValues[filter.field]?.from || null}
                  onChange={(newValue: Date | null) =>
                    handleFilterChange(filter.field, {
                      ...draftFilterValues[filter.field],
                      from: newValue,
                    })
                  }
                  slotProps={{
                    textField: { fullWidth: true, size: 'small' },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  label={`${filter.label} (To)`}
                  value={draftFilterValues[filter.field]?.to || null}
                  onChange={(newValue: Date | null) =>
                    handleFilterChange(filter.field, {
                      ...draftFilterValues[filter.field],
                      to: newValue,
                    })
                  }
                  slotProps={{
                    textField: { fullWidth: true, size: 'small' },
                  }}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        );

      case 'text':
        return (
          <TextField
            fullWidth
            label={filter.label}
            variant="outlined"
            size="small"
            value={draftFilterValues[filter.field] || ''}
            onChange={(e) => handleFilterChange(filter.field, e.target.value)}
          />
        );

      default:
        return (
          <Typography variant="body2" color="text.secondary">
            Unsupported filter type: {filter.type}
          </Typography>
        );
    }
  };

  return (
    <Box
      sx={{
        p: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderRadius: 1,
        overflow: 'auto',
        '& .MuiFormControl-root': { minHeight: '40px' },
        '& .MuiInputBase-root': { fontSize: '0.875rem' },
        '& .MuiInputLabel-root': { fontSize: '0.875rem' },
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <FilterIcon color="primary" sx={{ fontSize: '1.1rem' }} />
          <Typography
            variant="body2"
            fontWeight="600"
            sx={{ fontSize: '0.9rem' }}
          >
            {config.title || 'Filters & Controls'}
          </Typography>
        </Box>

        {config.filters && config.filters.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleClearFilters}
              startIcon={<ClearIcon />}
              size="small"
              sx={{
                textTransform: 'none',
                py: 0.25,
                px: 1,
                fontSize: '0.75rem',
                minHeight: '28px',
              }}
            >
              Clear All
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApplyFilters}
              disabled={!hasPendingChanges}
              startIcon={<FilterIcon />}
              size="small"
              sx={{
                textTransform: 'none',
                py: 0.25,
                px: 1,
                fontSize: '0.75rem',
                minHeight: '28px',
              }}
            >
              Apply Filters
            </Button>
          </Box>
        )}
      </Box>

      {config.description && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 1, fontSize: '0.75rem', lineHeight: 1.2 }}
        >
          {config.description}
        </Typography>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 1,
          mb: 1,
        }}
      >
        {config.filters &&
          config.filters.map((filter) => (
            <Box key={filter.field}>{renderFilter(filter)}</Box>
          ))}
      </Box>

      {config.actions && config.actions.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 1,
            mt: 1,
          }}
        >
          {config.actions.map((action, index) => (
            <Button
              key={index}
              variant={action.type === 'primary' ? 'contained' : 'outlined'}
              color={action.type === 'danger' ? 'error' : 'primary'}
              fullWidth
              size="small"
            >
              {action.label}
            </Button>
          ))}
        </Box>
      )}
    </Box>
  );
};
